import { useState, useEffect, useCallback } from 'react';
import { getSubmissions, updateSubmissionStatus } from '../../services/adminService';
import { SubmissionsTableSkeleton } from '../Skeleton';

const SubmissionsTable = () => {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [statusFilter, setStatusFilter] = useState('All');

    const fetchSubmissions = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getSubmissions();
            setSubmissions(data);
            setError('');
        } catch (err) {
            setError(err.message || 'Failed to fetch submissions');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubmissions();
    }, [fetchSubmissions]);

    const handleStatusChange = async (rowNumber, newStatus) => {
        try {
            await updateSubmissionStatus(rowNumber, newStatus);

            // Update local state
            setSubmissions(submissions.map(sub =>
                sub.rowNumber === rowNumber
                    ? { ...sub, 'Project Status': newStatus }
                    : sub
            ));
        } catch (err) {
            alert('Failed to update status: ' + err.message);
        }
    };

    const filteredSubmissions = submissions.filter(sub => {
        // Search filter
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = (
            sub['Team Name']?.toLowerCase().includes(searchLower) ||
            sub['Email']?.toLowerCase().includes(searchLower) ||
            sub['Deliverable Type']?.toLowerCase().includes(searchLower) ||
            sub['Payment Status']?.toLowerCase().includes(searchLower)
        );

        // Status filter
        const matchesStatus = statusFilter === 'All' || sub['Project Status'] === statusFilter;

        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return <SubmissionsTableSkeleton />;
    }

    if (error) {
        return (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 flex items-start gap-3">
                <span className="material-symbols-outlined text-red-400 text-2xl">error</span>
                <div>
                    <p className="text-red-400 font-semibold">Error Loading Submissions</p>
                    <p className="text-gray-400 text-sm mt-1">{error}</p>
                    <button
                        onClick={fetchSubmissions}
                        className="mt-3 text-sm text-blue-400 hover:text-blue-300"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-white text-2xl font-bold">Project Submissions</h2>
                    <p className="text-gray-400 text-sm mt-1">
                        {filteredSubmissions.length} {filteredSubmissions.length !== submissions.length && `of ${submissions.length}`} submissions
                    </p>
                </div>
                <button
                    onClick={fetchSubmissions}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                    <span className="material-symbols-outlined text-lg">refresh</span>
                    Refresh
                </button>
            </div>

            {/* Status Filters */}
            <div className="flex items-center gap-2 flex-wrap">
                {['All', 'Pending', 'In Progress', 'Completed', 'Delivered'].map((status) => {
                    const count = status === 'All'
                        ? submissions.length
                        : submissions.filter(sub => sub['Project Status'] === status).length;

                    return (
                        <button
                            key={status}
                            onClick={() => setStatusFilter(status)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${statusFilter === status
                                ? 'bg-white text-black'
                                : 'bg-dark-card border border-white/20 text-gray-400 hover:text-white hover:border-white/40'
                                }`}
                        >
                            {status} ({count})
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="flex items-center gap-3 bg-dark-card border border-border-gray rounded-xl p-4">
                <span className="material-symbols-outlined text-gray-400">search</span>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by team name, email, or project type..."
                    className="flex-1 bg-transparent text-white text-sm focus:outline-none placeholder:text-gray-600"
                />
            </div>

            {/* Table */}
            <div className="bg-dark-card border border-border-gray rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-dark-bg border-b border-border-gray">
                            <tr>
                                <th className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider px-6 py-4 w-32">Date</th>
                                <th className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider px-6 py-4 w-40">Team</th>
                                <th className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider px-6 py-4 w-48">Email</th>
                                <th className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider px-6 py-4 w-32">Project Type</th>
                                <th className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider px-6 py-4 w-36">Delivery</th>
                                <th className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider px-6 py-4 w-28">Amount</th>
                                <th className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider px-6 py-4 w-40">Status</th>
                                <th className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider px-6 py-4 w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-gray">
                            {filteredSubmissions.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-12 text-gray-400">
                                        No submissions found
                                    </td>
                                </tr>
                            ) : (
                                filteredSubmissions.map((submission) => {
                                    const isPriority = submission['Priority Delivery'] === 'Yes';
                                    return (
                                        <tr
                                            key={submission.rowNumber}
                                            className={`transition-all ${isPriority
                                                ? 'bg-gradient-to-r from-yellow-500/5 via-transparent to-yellow-500/5 border-l-4 border-yellow-500 hover:bg-yellow-500/10'
                                                : 'hover:bg-dark-bg'
                                                }`}
                                        >
                                            <td className="px-6 py-4 text-sm text-gray-300 w-32">
                                                {new Date(submission.Timestamp).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-white font-medium w-40">
                                                {submission['Team Name']}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-300 w-48">
                                                {submission.Email}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-300 w-32">
                                                {submission['Deliverable Type']}
                                            </td>
                                            <td className="px-6 py-4 w-36">
                                                {isPriority ? (
                                                    <div className="flex items-center gap-2">
                                                        <span className="material-symbols-outlined text-yellow-400 text-lg">bolt</span>
                                                        <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                                            PRIORITY
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-500 text-xs">Standard</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-white font-semibold w-28">
                                                ₹{submission['Final Amount (₹)']}
                                            </td>
                                            <td className="px-6 py-4 w-40">
                                                <div className="flex items-center gap-2">
                                                    <select
                                                        value={submission['Project Status'] || 'Pending'}
                                                        onChange={(e) => handleStatusChange(submission.rowNumber, e.target.value)}
                                                        className="bg-dark-bg border border-border-gray rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-white"
                                                    >
                                                        <option value="Pending">Pending</option>
                                                        <option value="In Progress">In Progress</option>
                                                        <option value="Completed">Completed</option>
                                                        <option value="Delivered">Delivered</option>
                                                    </select>
                                                    {(submission['Project Status'] === 'Completed' || submission['Project Status'] === 'Delivered') && (
                                                        <span className="material-symbols-outlined text-green-500 text-xl animate-pulse">
                                                            check_circle
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 w-32">
                                                <button
                                                    onClick={() => setSelectedSubmission(submission)}
                                                    className="text-blue-400 hover:text-blue-300 text-sm font-semibold"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Detail Modal */}
            {selectedSubmission && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6">
                    <div className="bg-dark-card border border-border-gray rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-dark-card border-b border-border-gray px-6 py-4 flex items-center justify-between">
                            <h3 className="text-white text-xl font-bold">Submission Details</h3>
                            <button
                                onClick={() => setSelectedSubmission(null)}
                                className="text-gray-400 hover:text-white"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {Object.entries(selectedSubmission).map(([key, value]) => {
                                if (key === 'rowNumber') return null;
                                return (
                                    <div key={key} className="border-b border-border-gray pb-3">
                                        <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">{key}</p>
                                        <p className="text-white text-sm">{value || 'N/A'}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubmissionsTable;
